import { useState } from "react";
import CopyButton, { kpiText } from "./CopyButton";

// Headline figures as a row of KPI cards, from the render_kpis tool (backend tools/visualization.py).
// spec = { title, cards: [{ label, value, unit?, caption?, change?, change_label?, higher_is_better? }] }

/** The value as written by the model, which has usually already scaled it (219.3 with unit "₹ Cr").
 *  Only an unscaled large number with no unit gets the Indian short form, so nothing is scaled twice. */
function formatValue(value, unit) {
  if (typeof value !== "number" || !isFinite(value)) return "—";
  const a = Math.abs(value);
  if (!unit && a >= 1e7) return (value / 1e7).toFixed(2).replace(/\.00$/, "") + " Cr";
  if (!unit && a >= 1e5) return (value / 1e5).toFixed(2).replace(/\.00$/, "") + " L";
  return value.toLocaleString("en-IN", { maximumFractionDigits: 2 });
}

/** "↑ 21.0% from last month", red or green by whether the move is good for this figure. */
function Change({ card }) {
  if (typeof card.change !== "number") return null;
  const up = card.change > 0;
  const flat = card.change === 0;
  const good = flat ? null : up === (card.higher_is_better !== false);
  const cls = "kc-change" + (good === null ? "" : good ? " good" : " bad");
  return (
    <div className={cls}>
      {flat ? "→" : up ? "↑" : "↓"} {Math.abs(card.change).toFixed(1)}%
      {card.change_label ? ` ${card.change_label}` : ""}
    </div>
  );
}

const DAY = new Intl.DateTimeFormat("en-IN", { day: "numeric", month: "short", year: "numeric" });
const fmtDay = (iso) => (iso ? DAY.format(new Date(`${iso}T00:00:00`)) : "…");
export const fmtPeriod = (p) => (p ? `${fmtDay(p.from)} – ${fmtDay(p.to)}` : "");

// "Total Billed Amount (Sep 2026)" and "Billed Amount" name the same measure.
const measureName = (label) =>
  String(label || "")
    .toLowerCase()
    .replace(/\([^)]*\)/g, " ")
    .replace(/^\s*total\s+/, "")
    .replace(/\s+/g, " ")
    .trim();

/**
 * Every KPI widget on a dashboard as ONE row of cards. Each chat that asked for KPIs produced its own
 * widget, but on a dashboard they are one set of headline numbers. A card is shown once even when
 * several widgets carry it — same measure, unit and value — and keeps `refs` to every copy
 * ({ widgetId, index }), so removing it removes all of them and a hidden copy can't reappear.
 */
/** A card's identity for ordering: what it measures and in which unit — not its value or position, which
 *  change on every refresh and every removal. */
export const cardKey = (card) => `${measureName(card.label)}|${card.unit || ""}`;

export function mergeKpiWidgets(widgets, order = []) {
  // One period for the whole row when every widget that HAS a period agrees — a widget with none (a
  // balance asked for "as of today") doesn't make the row's period ambiguous.
  const dated = widgets.filter((w) => w.spec?.period);
  const periods = new Set(dated.map((w) => fmtPeriod(w.spec.period)));
  const shared = periods.size === 1 ? dated[0].spec.period : null;
  const byKey = new Map();
  widgets.forEach((w) => {
    (w.spec?.cards || []).forEach((card, index) => {
      // Not keyed on the period: the same measure with the same value is the same figure even when one
      // copy was asked for "last month" and another with no period at all — a balance as of today
      // (outstanding, unadjusted) does not depend on the period, and showed up twice when it was keyed.
      const key = [measureName(card.label), card.unit || "", card.value].join("|");
      const hit = byKey.get(key);
      if (hit) hit.refs.push({ widgetId: w.id, index });
      else byKey.set(key, { ...card, period: shared ? null : w.spec?.period, refs: [{ widgetId: w.id, index }] });
    });
  });
  const narration = [...new Set(widgets.map((w) => (w.spec?.narration || "").trim()).filter(Boolean))];
  // The owner's dragged order first; any card not in it yet (from a chat added later) keeps its natural
  // place after them. Array.prototype.sort is stable, so ties keep the natural order.
  const rank = new Map(order.map((k, i) => [k, i]));
  const cards = [...byKey.values()].sort(
    (a, b) => (rank.get(cardKey(a)) ?? Infinity) - (rank.get(cardKey(b)) ?? Infinity)
  );
  return { title: "", period: shared, cards, narration: narration.join("\n\n") };
}

/** On a saved dashboard: `onRemoveCard(index, card)` puts a remove button on each card, and
 *  `onMoveCard(from, to)` lets the cards be dragged into a new order. */
export default function KpiWidget({ spec, onRemoveCard, onMoveCard }) {
  // { from, over, x0, y0, active } while a card is held down; null otherwise.
  const [drag, setDrag] = useState(null);
  const cards = spec?.cards || [];
  if (!cards.length) return null;
  const period = spec.period;
  const movable = Boolean(onMoveCard) && cards.length > 1;
  const dragFrom = drag?.active ? drag.from : null;
  const dragOver = drag?.active ? drag.over : null;

  // Pointer events rather than HTML5 drag-and-drop: native DnD did not reliably start on these tiles,
  // and it never works on touch screens. Press a card, move past a few pixels (so a click is still a
  // click), release over another card. Pointer capture keeps the moves coming to the pressed card even
  // once the pointer leaves it; elementFromPoint finds which card is underneath.
  const dragProps = (i) =>
    movable
      ? {
          "data-index": i,
          onPointerDown: (e) => {
            if (e.button !== 0 || e.target.closest(".kc-remove")) return;
            e.currentTarget.setPointerCapture?.(e.pointerId);
            setDrag({ from: i, over: i, x0: e.clientX, y0: e.clientY, active: false });
          },
          onPointerMove: (e) => {
            if (!drag) return;
            const moved = Math.abs(e.clientX - drag.x0) + Math.abs(e.clientY - drag.y0) > 6;
            if (!moved && !drag.active) return;
            const under = document.elementFromPoint(e.clientX, e.clientY)?.closest(".kc-tile[data-index]");
            const over = under ? Number(under.dataset.index) : drag.over;
            if (!drag.active || over !== drag.over) setDrag({ ...drag, active: true, over });
          },
          onPointerUp: () => {
            if (drag?.active && drag.over !== drag.from) onMoveCard(drag.from, drag.over);
            setDrag(null);
          },
          onPointerCancel: () => setDrag(null),
        }
      : {};
  return (
    <div className="kc-card">
      {!onMoveCard && <CopyButton className="on-card" title="Copy figures" getText={() => kpiText(spec)} />}
      {(spec.title || period) && (
        <div className="kc-title">
          {spec.title}
          {period && <span className="kc-period">{fmtPeriod(period)}</span>}
        </div>
      )}
      <div className="kc-grid">
        {cards.map((card, i) => (
          <div
            key={`${card.label}-${i}`}
            className={
              "kc-tile" +
              (movable ? " movable" : "") +
              (dragFrom === i ? " is-dragging" : "") +
              (dragOver === i && dragFrom !== i ? " is-drop-target" : "")
            }
            title={movable ? "Drag to rearrange" : undefined}
            {...dragProps(i)}
          >
            {onRemoveCard && (
              <button
                type="button"
                className="kc-remove"
                title={`Remove "${card.label}"`}
                aria-label={`Remove ${card.label}`}
                onClick={() => onRemoveCard(i, card)}
              >
                ✕
              </button>
            )}
            <div className="kc-label">{card.label}</div>
            <div className="kc-value">
              {formatValue(card.value, card.unit)}
              {card.unit && <span className="kc-unit">{card.unit}</span>}
            </div>
            {card.caption && <div className="kc-caption">{card.caption}</div>}
            {card.period && <div className="kc-caption">{fmtPeriod(card.period)}</div>}
            <Change card={card} />
          </div>
        ))}
      </div>
    </div>
  );
}
